import { Alert, Typography } from "antd";
import React from "react";
import { useEditContext, usePermissions } from "react-admin";
import { ServiceItemDetailProps, RoutableTabConfig } from '@src/types';
import { ResponsesTable } from "../Common/Integrations";
import VersionizedObjectsTab from "../Common/VersionizedObjectsTab";
import ChangeInstancesTab from "./SubComponents/ChangeInstancesTab";
import Declarations from "./SubComponents/Declarations";
import Metadata from "./SubComponents/Metadata";
import ServiceItemTitleCard from "./SubComponents/ServiceItemTitleCard";
import RoutableTabs from "../Common/RoutableTabs";
import MainCard from "../Common/MainCard";
import { CounterpartyProvider } from "../Teams/Counterparty";
import PackPipelineDetail from "@src/components/AIProcessors/SubComponents/PackPipelineDetail";
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";
import { usePackAccess } from "@src/components/AIProcessors/packAccess";
import "./DetailView.css";

const { Text } = Typography;

const ServiceItemDetailCard = ({ pov, serviceItemsResource, changeInstancesResource, deployedItemsResource, healthcheckResource, healthcheckResponsesResource }: ServiceItemDetailProps) => {
    const { record, loading } = useEditContext<any>();
    const { permissions } = usePermissions();
    const generalSettings = useGetGeneralSettings();
    const AIIntegrationSettings = generalSettings.find(setting => setting.name == 'AIIntegration')

    /* Whether the Pack tab belongs on this page at all, and whether it can be
       driven from it. One answer for both points of view — see packAccess.ts.
       Hooks run before the record does, hence the optional chaining; the guard
       below keeps the tabs from being built until the record is in. */
    const packAccess = usePackAccess({
        pov,
        permissions,
        aiIntegrationEnabled: !!AIIntegrationSettings?.config?.show_ai_processor,
        hasAiProcessors: !!record?.has_ai_processors,
        serviceId: record?.service?.id,
    });

    const getMessageForServiceState = (state: string) => {
        if (state === "NO_NEW_SERVICE_ITEM")
            return <>The Service {<Text strong>{record?.service?.name}</Text>} is no longer accepting new Service Items. Consumers can still modify or delete existing Service Items.</>

        else if (state === "NO_MODIFY_EXISTING_SERVICE_ITEM")
            return <>Consumers can only decommission existing service items for the {<Text strong>{record?.service?.name}</Text>} service. Creation and modification are not allowed.</>

        else if (state === "DECOMMISSIONED")
            return <>The Service {<Text strong>{record?.service?.name}</Text>} has been fully decommissioned and is no longer available.</>
    }

    if (loading || !record) return null;

    const tabs: RoutableTabConfig[] = [
        {
            key: 'declarations',
            tab: "Declarations",
            content: <Declarations resource={serviceItemsResource} service_item={record} />,
        },
        {
            key: 'change_instances',
            tab: "Change Instances",
            content: (
                <ChangeInstancesTab
                    resource={changeInstancesResource}
                    baseFilter={{ service_item_id: record.id }}
                    submissionPageRedirectVisible={pov === "consumer"}
                />
            ),
        },
        {
            key: 'deployed_items',
            tab: "Deployed Items",
            content: (
                <VersionizedObjectsTab
                    resource={deployedItemsResource}
                    light={true}
                    name={"deployed item"}
                    attrName={"data"}
                    isCreate={pov === "serviceowner" && permissions === "ADMIN"}
                    record={record}
                    pov={pov}
                />
            ),
        },
        {
            key: 'metadata',
            tab: "Metadata",
            content: <Metadata record={record} />,
        },
    ];

    if (record?.service?.healthcheck)
        tabs.push({
            key: 'healthcheck_responses',
            tab: "Healthcheck Responses",
            content: (
                // The header above names the item and its service, so the log
                // carries neither: what is left is the response itself — what
                // came back, what state it is in, and when.
                <ResponsesTable
                    config={{
                        resource: healthcheckResponsesResource,
                        emptyText: "This item has not been checked yet.",
                    }}
                    filter={{ service_item_id: record.id }}
                />
            ),
        })

    if (packAccess.visible)
        tabs.push({
            key: 'netorca_pack',
            tab: "NetOrca Pack",
            content: (
                <PackPipelineDetail
                    service_item_id={record.id}
                    service_id={record.service?.id}
                    pov={pov}
                    readOnly={packAccess.readOnly}
                />
            ),
        })

    return (
        // the header names one counterparty, and the panel opens over the page
        <CounterpartyProvider>
        <div className="netorca-sid netorca-frame">
            {record.is_service_private && (
                <Alert
                    className="sid-alert"
                    message="Service has become private"
                    description="The service has become private and your team is not in the service's allowed teams list"
                    type="error"
                    showIcon
                />
            )}
            {record.service && record.service.state !== "IN_SERVICE" && (
                <Alert
                    className="sid-alert"
                    message={getMessageForServiceState(record.service.state)}
                    type="warning"
                    showIcon
                />
            )}

            <ServiceItemTitleCard record={record} pov={pov} healthcheckResource={healthcheckResource} />

            <MainCard className="sid-panel">
                <RoutableTabs className="sid-tabs" tabs={tabs} defaultActiveKey="declarations" />
            </MainCard>
        </div>
        </CounterpartyProvider>
    )
}

export default ServiceItemDetailCard;
